import { FC, useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { checkAccessTokenExpiration, useAuth } from 'context/Auth/AuthContext';
import FullScreenLoader from 'components/Loaders/FullScreenLoader';
import AccessDenied from 'pages/Authentication/AccessDenied';
import routes from 'routes';
import { logout } from 'api/auth';

const PrivateRoute: FC = () => {
  const { user, setUser, isUserLoading, accessDenied } = useAuth();
  const [isSessionLoading, setIsSessionLoading] = useState<boolean>(true);
  const [isAccessTokenValid, setIsAccessTokenValid] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const verifySession = async () => {
      setIsSessionLoading(true);

      const token = checkAccessTokenExpiration();
      if (!token) {
        try {
          await logout();
        } catch (error) {
          // Best-effort sign-out; redirect fallback is handled below.
        }
        if (isMounted) {
          setIsAccessTokenValid(false);
          setIsSessionLoading(false);
        }
        return;
      }

      if (user.username) {
        if (isMounted) {
          setIsAccessTokenValid(true);
          setIsSessionLoading(false);
        }
        return;
      }

      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        if (!isMounted) {
          return;
        }
        setUser(currentUser);
        setIsAccessTokenValid(true);
      } catch (error) {
        try {
          await logout();
        } catch (logoutError) {
          // Best-effort sign-out; redirect fallback is handled below.
        }
        if (isMounted) {
          setIsAccessTokenValid(false);
        }
      } finally {
        if (isMounted) {
          setIsSessionLoading(false);
        }
      }
    };

    verifySession();

    return () => {
      isMounted = false;
    };
  }, [setUser, user.username]);

  if (!isAccessTokenValid) {
    return <Navigate to={routes.auth.logIn} replace />;
  }

  if (isSessionLoading || isUserLoading) {
    return <FullScreenLoader />;
  }

  // Authenticated via Google but not authorised for the data tool.
  if (accessDenied) {
    return <AccessDenied />;
  }

  if (!user?.username) {
    return <Navigate to={routes.auth.logIn} replace />;
  }

  return <Outlet />;
};

export default PrivateRoute;
